/**
 * AdsAccountPicker — reusable Google Ads customer-id picker.
 *
 * Drop-in component for binding a Customer record to a Google Ads
 * account. Loads the user's accessible MCC tree once on mount, renders
 * a labeled <select>, and emits the normalised (digits-only) customer id
 * + descriptive name on change.
 *
 * Use sites:
 *   - NewCustomerModal — field on the create/edit form
 *   - ClientDetailPanel — inline binding control on the header
 *   - ClientAdsTab — empty-state "bind this customer" CTA
 *
 * Surface decisions:
 *   - Disabled while loading (so the form can't submit a stale "" value).
 *   - Manager / sub-manager rows are shown but visually marked and (by
 *     default) NOT selectable — binding a customer to a manager doesn't
 *     work for metric queries. Override via `allowManagerSelection` if
 *     a use case ever needs to bind an MCC explicitly.
 *   - "Unbind" option always available so the user can clear a binding
 *     after a customer relationship ends.
 *   - Errors surface inline below the picker — never throws.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AccessibleCustomer,
  EnrichedAccount,
  describeAccount,
  loadAdsAccountList,
} from './googleAdsApi';

export interface AdsAccountPickerProps {
  /** Bound customer id (digits only, no dashes), or empty / undefined. */
  value: string | undefined | null;
  /** Fired on user pick. Empty string = "Unbind" (cleared). The label is
   *  the descriptive name (or pretty id if no name) suitable for caching
   *  on the Customer doc. */
  onChange: (customerId: string, label: string) => void;
  /** Override the default empty-option label. */
  placeholder?: string;
  /** When true, manager / sub-manager accounts are selectable. Default
   *  false (binding to a manager produces a dead-end since metric queries
   *  return REQUESTED_METRICS_FOR_MANAGER). */
  allowManagerSelection?: boolean;
  /** Force-disable the picker (e.g. while a parent form is saving). */
  disabled?: boolean;
  /** Extra className on the <select> for styling parity with the parent. */
  className?: string;
  /** Compact mode hides the helper text below the picker. */
  compact?: boolean;
}

export const AdsAccountPicker: React.FC<AdsAccountPickerProps> = ({
  value,
  onChange,
  placeholder = '— not bound —',
  allowManagerSelection = false,
  disabled,
  className,
  compact,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<AccessibleCustomer[]>([]);
  const [enriched, setEnriched] = useState<Record<string, EnrichedAccount>>({});
  const [managerCid, setManagerCid] = useState('');
  const [knownManagers, setKnownManagers] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await loadAdsAccountList();
      if (r.error) setError(r.error);
      setAccounts(r.displayAccounts);
      setEnriched(r.enriched);
      setManagerCid(r.managerCid);
      setKnownManagers(r.knownManagers);
    } catch (e: any) {
      setError(e?.message || 'Could not load Google Ads accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Sort: leaves first (the common bind target), then sub-managers, then
  // top manager. Within each group, alphabetical by descriptive name
  // (id as fallback) so the dropdown reads predictably.
  const sortedAccounts = useMemo(() => {
    const rank = (cid: string): number => {
      if (cid === managerCid) return 2; // top MCC last
      if (knownManagers.has(cid)) return 1; // sub-managers middle
      return 0; // operating accounts first
    };
    const label = (cid: string): string =>
      (enriched[cid]?.descriptiveName || cid).toLowerCase();
    return [...accounts].sort((a, b) => {
      const ra = rank(a.customerId);
      const rb = rank(b.customerId);
      if (ra !== rb) return ra - rb;
      return label(a.customerId).localeCompare(label(b.customerId));
    });
  }, [accounts, enriched, managerCid, knownManagers]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCid = e.target.value;
    if (!newCid) { onChange('', ''); return; }
    const meta = enriched[newCid];
    const name = meta?.descriptiveName || '';
    onChange(newCid, name);
  };

  return (
    <div className="ads-account-picker">
      <select
        className={className || 'form-select form-select-sm'}
        value={value || ''}
        onChange={handleChange}
        disabled={disabled || loading}
      >
        {loading && <option value="">loading Google Ads accounts…</option>}
        {!loading && (
          <>
            <option value="">{placeholder}</option>
            {sortedAccounts.map((a) => {
              const isMgr = knownManagers.has(a.customerId);
              const label = describeAccount(a.customerId, enriched, managerCid, knownManagers);
              // Disable managers unless explicitly allowed — binding a
              // customer to a manager doesn't unlock useful queries.
              const isDisabled = isMgr && !allowManagerSelection;
              return (
                <option key={a.customerId} value={a.customerId} disabled={isDisabled}>
                  {label}{isDisabled ? ' (not bindable)' : ''}
                </option>
              );
            })}
          </>
        )}
      </select>
      {error && (
        <div className="text-danger fs-8 mt-1">{error}</div>
      )}
      {!compact && !loading && !error && (
        <div className="text-muted fs-8 mt-1">
          Sub-managers and the top MCC aren't bindable — pick a leaf operating account.
        </div>
      )}
    </div>
  );
};

export default AdsAccountPicker;
